import React, { useContext, useEffect, useMemo, useState } from "react"
import Geocode from "react-geocode"

import { useRecoilValue } from "recoil"
import {
  is3PLsEnabledSelector,
  isKosmoNetworkSelector,
  isManagerSelector,
  isManagerWithLocationSelector,
  isOwnerAtom,
  userAtom,
  isSupportRoleSelector,
  isPayingUserSelector,
} from "@/atoms/userAtom"

import { ITab } from "@/modules/shared/Tabs.container"
import { useTranslation } from "react-i18next"
import { SandboxContext } from "@/layout/Nav/SandboxSwitcher"
import DispatchSettings from "@/modules/profile/DispatchSettings/DispatchSettings.container"
import GeneralSettings from "@/modules/profile/GeneralSettings/GeneralSettings.container"
import ProviderSettings from "@/modules/profile/ProviderSettings/ProviderSettings.container"
import LocationSettings from "@/modules/profile/LocationSettings/LocationSettings"
import OrganizationSettings from "@/modules/profile/OrganizationSettings/OrganizationSettings.container"
import { useFlags } from "flagsmith/react"
import EcommerceSettings from "@/modules/profile/EcommerceSettings/EcommerceSettings.container"
import PlansSettings from "@/modules/profile/PlansSettings/PlansSettings.container"
import NotificationsTabs from "@/modules/profile/NotificationsSettings/NotificationsTabs.container"
import { Menu, MenuProps, message } from "antd"
import {
  SettingOutlined,
  TeamOutlined,
  ToolOutlined,
  FileTextOutlined,
  UserOutlined,
} from "@ant-design/icons"
import { useInterval, useMediaQuery } from "usehooks-ts"
import { useSandboxRouter } from "@/hooks/useSandboxRouter"
import { isEmpty } from "lodash"
import DriversSettings from "@/modules/profile/DriversSettings/DriversSettings.container"
import RouteSettings from "@/modules/profile/RouteSettings/RouteSettings.container"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCode, faHeadset, faHeartPulse } from "@fortawesome/free-solid-svg-icons"
import { DEVELOPERS_DOC_URL, KOSMO_STATUS_URL, TAB_EXTERNAL_LINKS } from "@/constants/routes"
import APIKeys from "@/modules/developers/APIKeys/APIKeys.container"
import Webhooks from "@/modules/developers/Webhooks.container"
import APIKeysSvg from "@/modules/developers/assets/APIKeys.svg"
import DocsSvg from "@/modules/developers/assets/Docs.svg"
import WebhooksSvg from "@/modules/developers/assets/Webhooks.svg"
import TeamSettings from "@/modules/profile/TeamSettings/TeamSettings.container"
import { useStripeBillingPortal } from "@/hooks/useStripeBillingPortal"
import DriverPaySettings from "@/modules/profile/DriverAppSettings/DriverPaySettings.container"
import DriverAppSettings from "@/modules/profile/DriverAppSettings/DriverAppSettings.container"
import { useUser } from "@/hooks/useUser"
import { DefaultPaymentMethodType } from "@/types/paymentMethod.types"
import ZonesSettings from "@/modules/profile/ZonesSettings/ZonesSettings.container"
import ConfirmingSubscriptionModal from "@/modules/profile/shared/ConfirmingSubscriptionModal"
import MenuTabTag from "@/modules/profile/shared/MenuTabTag.component"
import PreventWithModal from "@/UI/PreventWithModal"
import BrandingSettings from "@/modules/profile/BrandingSettings/BrandingSettings"
import ClientsPage from "../client-management/ClientsPage"

const { initIntercomWindow, loadIntercom, showIntercomWindow } = require("next-intercom")
const buildMenu = (tabs: ITab[]): MenuProps["items"] => {
  return tabs.map((tab) => {
    if (!tab.visible) {
      return null
    }
    if (tab.children) {
      return {
        label: (
          <span>
            {tab.title}
            {tab.isBeta && <MenuTabTag type={"beta"} />}
          </span>
        ),
        key: tab.key,
        icon: tab.icon,
        disabled: tab.disabled,
        children: buildMenu(tab.children),
      }
    }
    return {
      label: (
        <span>
          {tab.title}
          {tab.isBeta && <MenuTabTag type={"beta"} />}
          {tab.isNew && <MenuTabTag type={"new"} />}
        </span>
      ),
      key: tab.key,
      icon: tab.icon,
      disabled: tab.disabled,
    }
  })
}

const findTab = (tabs: ITab[], key: string): ITab | undefined => {
  for (const tab of tabs) {
    if (tab.key === key) {
      return tab
    }
    if (tab.children) {
      const found = findTab(tab.children, key)
      if (found) {
        return found
      }
    }
  }
  return undefined
}

const ProfileContainer: React.FC = () => {
  const { t } = useTranslation()

  const user = useRecoilValue(userAtom)
  const { fetchUserProfile } = useUser(true)

  Geocode.setApiKey(process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "")

  const { sandboxRouter } = useSandboxRouter()

  const isKosmoNetwork = useRecoilValue(isKosmoNetworkSelector)
  const is3PlsEnabled = useRecoilValue(is3PLsEnabledSelector)
  const sandbox = useContext(SandboxContext)
  const isOwner = useRecoilValue(isOwnerAtom)
  const isManager = useRecoilValue(isManagerSelector)
  const [isManagerWithLocation, _] = useRecoilValue(isManagerWithLocationSelector)
  const flags = useFlags([
    "show_notifications_settings",
    "show_shopify",
    "show_ecommerce_add_store",
    "show_driver_app_settings",
    "show_driver_pay_settings",
    "show_branding_settings",
  ])

  const { redirectToBillingPortal } = useStripeBillingPortal(isOwner)

  const [activeTab, setActiveTab] = useState<string>("general")
  const isSupportRole = useRecoilValue(isSupportRoleSelector)
  const isPayingUser = useRecoilValue(isPayingUserSelector)
  const isLimitedRole = isManager || isSupportRole

  const tabs: ITab[] = [
    {
      title: t("profile.Profile.general"),
      component: <GeneralSettings />,
      visible: true,
      disabled: false,
      id: "general-tab",
      key: "general",
      icon: <SettingOutlined />,
    },
    {
      title: t("profile.common.organization"),
      component: <OrganizationSettings />,
      visible: !isSupportRole,
      disabled: sandbox.enabled,
      id: "organization-tab",
      key: "organization",
      icon: <TeamOutlined />,
    },
    {
      title: t("profile.common.clientManagement"),
      visible: !isLimitedRole,
      id: "clients-management-tab",
      key: "clients-management",
      disabled: false,
      icon: <UserOutlined />,
      children: [
        {
          title: t("common.clients"),
          component: <ClientsPage />,
          visible: !isLimitedRole,
          id: "clients-tab",
          key: "clients",
        },
        {
          title: t("common.clientsPricing"),
          component: <DriverPaySettings type="clients" />,
          visible: !isLimitedRole,
          id: "clients-pricing-tab",
          key: "clients-pricing",
        },
      ],
    },
    {
      title: t("profile.common.operations"),
      visible: !isSupportRole,
      id: "operations-tab",
      key: "operations",
      disabled: false,
      icon: <ToolOutlined />,
      children: [
        {
          title: t("common.locations"),
          component: <LocationSettings />,
          visible: !isManagerWithLocation,
          id: "locations-tab",
          key: "locations",
        },
        {
          title: t("common.zones"),
          component: <ZonesSettings />,
          visible: true,
          disabled: sandbox.enabled,
          id: "zones-tab",
          key: "zones",
        },
        {
          title: t("common.teams"),
          component: <TeamSettings />,
          visible: true,
          id: "team-settings-tab",
          key: "team-settings",
        },
        {
          title: t("common.drivers"),
          component: <DriversSettings />,
          visible: true,
          id: "drivers-tab",
          key: "drivers",
        },
        {
          title: t("profile.common.driverAppSettings"),
          component: <DriverAppSettings />,
          visible: flags.show_driver_app_settings.enabled && !isLimitedRole,
          disabled: sandbox.enabled,
          id: "driver-app-settings-tab",
          key: "driver-app-settings",
        },
        {
          title: t("profile.common.driverPay"),
          component: <DriverPaySettings />,
          visible: flags.show_driver_pay_settings.enabled && !isLimitedRole,
          disabled: sandbox.enabled,
          id: "driver-pay-settings-tab",
          key: "driver-pay-settings",
        },
        {
          title: t("profile.common.routeSettings"),
          component: <RouteSettings />,
          visible: !isLimitedRole,
          disabled: sandbox.enabled,
          id: "route-settings-tab",
          key: "route-settings",
        },
        {
          title: t("common.couriers"),
          component: <ProviderSettings />,
          visible: is3PlsEnabled && !isKosmoNetwork && !isLimitedRole,
          disabled: sandbox.enabled || !isPayingUser,
          id: "carriers-tab",
          key: "couriers",
        },
        {
          title: t("profile.DispatchSettings.dispatchSettings"),
          component: <DispatchSettings />,
          visible: !isLimitedRole && !isKosmoNetwork,
          id: "dispatch-rules-tab",
          key: "dispatch",
        },
        {
          title: (
            <PreventWithModal
              enable={!isPayingUser}
              label={t("onboarding.freePlanBannerDescription")}
              cta={{
                label: t("profile.planCard.startNow"),
                onClick: () => {},
              }}
            >
              <p>{t("profile.NotificationsSettings.notifications")}</p>
            </PreventWithModal>
          ),
          component: <NotificationsTabs />,
          visible: flags.show_notifications_settings.enabled && !isLimitedRole,
          disabled: sandbox.enabled,
          id: "notifications-tab",
          key: "notifications",
        },
        {
          title: t("common.trackingPage"),
          visible:
            !sandbox.enabled &&
            flags.show_branding_settings.enabled &&
            isPayingUser &&
            !isLimitedRole &&
            !user.customSubdomain,
          id: "branding-tab",
          key: "branding",
          component: <BrandingSettings />,
        },
        {
          title: t("common.ecommerce.shopify"),
          component: <EcommerceSettings />,
          visible: flags.show_shopify.enabled && !isLimitedRole,
          disabled: sandbox.enabled,
          id: "ecommerce-tab",
          key: "ecommerce",
        },
      ],
    },
    {
      title: t("profile.common.developers"),
      visible: !isLimitedRole,
      id: "developers-tab",
      key: "developers",
      disabled: false,
      icon: <FontAwesomeIcon icon={faCode} />,
      children: [
        {
          id: "API Keys",
          key: "api-keys",
          title: t("developers.apiKeys"),
          icon: (
            <div className="mr-0.5">
              <APIKeysSvg fill={activeTab == "api-keys" ? "#111827" : "#9CA3AF"} />
            </div>
          ),
          component: <APIKeys />,
          visible: true,
          disabled: !isPayingUser,
        },
        {
          id: "Webhooks",
          key: "webhooks",
          title: t("developers.webhooks"),
          icon: (
            <div className="mr-0.5">
              <WebhooksSvg fill={activeTab == "webhooks" ? "#111827" : "#9CA3AF"} />
            </div>
          ),
          component: <Webhooks />,
          visible: true,
          disabled: !isPayingUser,
        },
        {
          id: "Docs",
          key: DEVELOPERS_DOC_URL,
          title: t("developers.docs"),
          icon: (
            <div className="mr-0.5">
              <DocsSvg fill={activeTab == "3" ? "#111827" : "#9CA3AF"} />
            </div>
          ),
          component: <></>,
          visible: true,
          disabled: false,
        },
        {
          id: "Live Status",
          key: KOSMO_STATUS_URL,
          title: t("developers.liveStatus"),
          icon: (
            <FontAwesomeIcon
              style={activeTab == "4" ? { color: "#111827" } : { color: "#9CA3AF" }}
              icon={faHeartPulse}
            />
          ),
          component: <></>,
          visible: true,
          disabled: false,
        },
      ],
    },
    {
      title: t("profile.PaymentSettings.billing"),
      visible: isOwner && !sandbox.enabled,
      id: "billing-menu-tab",
      key: "billing-menu",
      icon: <FileTextOutlined />,
      children: [
        {
          title: t("profile.PlansSettings.plansAndPricing"),
          component: <PlansSettings />,
          visible: isOwner,
          disabled: sandbox.enabled,
          id: "plans-tab",
          key: "plans",
        },
        {
          title: t("profile.PaymentSettings.billing"),
          component: <></>,
          visible: isOwner && user.org.hasStripeAccount,
          disabled: sandbox.enabled || !user?.org?.hasStripeAccount,
          id: "billing-tab",
          key: "billing",
        },
      ],
    },
    {
      title: t("common.support"),
      component: <></>,
      icon: <FontAwesomeIcon icon={faHeadset} />,
      visible: !!process.env.NEXT_PUBLIC_INTERCOM_APPID,
      disabled: false,
      id: "support-intercom",
      key: "support-intercom",
    },
  ]
  // initIntercomWindow({
  //   appId: process.env.NEXT_PUBLIC_INTERCOM_APPID,
  //   email: user?.email,
  //   user_id: user?.id,
  //   company: {
  //     company_id: user?.org?.id,
  //     name: user?.org?.name,
  //     industry: user?.org?.country,
  //     website: user?.org?.email,
  //   },
  //   companies: user?.orgs?.map((org) => ({
  //     company_id: org?.id,
  //     name: org?.name,
  //     industry: org?.country,
  //   })),
  // })

  const handleChangeTab = async ({ key }: { key: string }) => {
    if (key === "notifications" && !isPayingUser) {
      setActiveTab("plans")
      sandboxRouter?.pushWithContext({
        targetHref: sandboxRouter.pathname,
        query: { ...sandboxRouter.query, p: "plans" },
      })
      return
    }

    if (key === "support-intercom") {
      if (process.env.NEXT_PUBLIC_INTERCOM_APPID) {
        loadIntercom({
          appId: process.env.NEXT_PUBLIC_INTERCOM_APPID, // default : ''
          ssr: false, // default: false
          initWindow: false, // default: true
          delay: 0, // default: 0  - usefull for mobile devices to prevent blocking the main thread
        })
      }

      initIntercomWindow({
        appId: process.env.NEXT_PUBLIC_INTERCOM_APPID,
        email: user?.email,
        user_id: user?.id,
        company: {
          company_id: user?.org?.id,
          name: user?.org?.name,
          industry: user?.org?.country,
          website: user?.org?.email,
        },
        companies: user?.orgs?.map((org) => ({
          company_id: org?.id,
          name: org?.name,
          industry: org?.country,
        })),
      })

      showIntercomWindow()
      return
    }

    if (findTab(tabs, activeTab)?.component?.key) {
      return
    }

    if (TAB_EXTERNAL_LINKS.includes(key)) {
      window.open(key, "_blank")?.focus()
    }
    // Redirects to stripe portal
    if (key === "billing" && user.org.hasStripeAccount) {
      await redirectToBillingPortal()
      setActiveTab("plans")
      sandboxRouter?.pushWithContext({
        targetHref: sandboxRouter.pathname,
        query: { ...sandboxRouter.query, p: "plan" },
      })
      return
    }

    sandboxRouter?.pushWithContext({
      targetHref: sandboxRouter.pathname,
      query: { ...sandboxRouter.query, p: key },
    })
    setActiveTab(key as string)
  }

  const activeComponent = useMemo(() => {
    return findTab(tabs, activeTab)?.component
  }, [tabs, activeTab])

  let isMobile = useMediaQuery("(max-width: 1080px)")

  useEffect(() => {
    if (!isPayingUser && !isSupportRole) {
      setActiveTab("plans")
      return
    }
  }, [user.org?.id])

  useEffect(() => {
    const targetTab = findTab(tabs, sandboxRouter?.query?.p as string)
    if (targetTab?.key && !isEmpty(targetTab) && targetTab?.visible) {
      setActiveTab(targetTab.key)
    } else {
      setActiveTab("general")
      sandboxRouter?.pushWithContext({
        targetHref: sandboxRouter.pathname,
        query: { ...sandboxRouter.query, p: "general" },
      })
    }
  }, [sandboxRouter.query, sandbox.enabled])

  const [pollingCount, setPollingCount] = useState(20)
  const urlParams = new URLSearchParams(window.location.search)
  const successCheckout = urlParams.get("success_checkout")

  const isCardOrgWithCard =
    user?.preference?.defaultPaymentMethod !== DefaultPaymentMethodType.BANK_TRANSFER &&
    user?.org.hasValidPaymentMethod
  const isBillingProfileComplete = isCardOrgWithCard && user?.org?.hasActiveSubscription

  const shouldPoll =
    !isBillingProfileComplete && pollingCount >= 0 && Boolean(successCheckout) === true
  const POLLING_EXECUTION_INTERVAL = 1

  useInterval(
    async () => {
      if (pollingCount === 0) {
        message.error(t("errors.pollingSubscription"))
      }
      setPollingCount((state) => state - 1)
      return fetchUserProfile()
    },
    shouldPoll ? 1000 * POLLING_EXECUTION_INTERVAL : null
  )

  return (
    <>
      <Menu
        onClick={handleChangeTab}
        {...(!isMobile ? { style: { width: 300, overflowX: "hidden" } } : {})}
        mode={isMobile ? "horizontal" : "inline"}
        items={buildMenu(tabs)}
        selectedKeys={[activeTab]}
        defaultOpenKeys={!isPayingUser ? ["billing-menu"] : !isMobile ? ["operations"] : []}
        className={`pt-3 ${isMobile ? "overflow-y-hidden" : "overflow-y-auto"}`}
      />
      <div className="flex-1 overflow-y-auto">{activeComponent}</div>
      <ConfirmingSubscriptionModal open={shouldPoll} />
    </>
  )
}

export default ProfileContainer
